<template>
  <div class="registration-container">
    <!-- Progress Indicator -->
    <div class="progress-section">
      <div class="progress-container">
        <div class="progress-step active">
          <span class="step-number">1</span>
        </div>
        <div class="progress-line"></div>
        <div class="progress-step">
          <span class="step-number">2</span>
        </div>
        <div class="progress-line"></div>
        <div class="progress-step">
          <span class="step-number">3</span>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="content-wrapper">
      <div class="header-section">
        <div class="back-arrow-circle">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="3"
            stroke="currentColor"
            class="back-arrow-icon"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
            />
          </svg>
        </div>
        <div class="title-container">
          <h1 class="main-title">Student Registration</h1>
          <p class="subtitle">Join our learning community and get the help you need to succeed!</p>
        </div>
      </div>

      <!-- Main Form Card -->
      <div class="form-card">
        <!-- Personal Information Section -->
        <div class="form-section">
          <div class="section-header">
            <div class="section-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                />
              </svg>
            </div>
            <div class="section-title">
              <h3>Personal Information</h3>
              <p>Tell us about yourself</p>
            </div>
          </div>

          <div class="form-grid">
            <div class="form-group">
              <label for="firstName">First Name <span class="required">*</span></label>
              <input
                id="firstName"
                v-model="firstName"
                type="text"
                required
                maxlength="50"
                :class="{ error: errors.firstName }"
                placeholder="Enter your first name"
                @input="validateNameField(firstName, 'firstName')"
                @blur="validateNameField(firstName, 'firstName')"
              />
              <span v-if="errors.firstName" class="error-message">{{ errors.firstName }}</span>
            </div>

            <div class="form-group">
              <label for="middleName">Middle Name</label>
              <input
                id="middleName"
                v-model="middleName"
                type="text"
                maxlength="50"
                :class="{ error: errors.middleName }"
                placeholder="Enter your middle name"
                @input="validateNameField(middleName, 'middleName')"
                @blur="validateNameField(middleName, 'middleName')"
              />
              <span v-if="errors.middleName" class="error-message">{{ errors.middleName }}</span>
            </div>

            <div class="form-group">
              <label for="lastName">Last Name <span class="required">*</span></label>
              <input
                id="lastName"
                v-model="lastName"
                type="text"
                required
                maxlength="50"
                :class="{ error: errors.lastName }"
                placeholder="Enter your last name"
                @input="validateNameField(lastName, 'lastName')"
                @blur="validateNameField(lastName, 'lastName')"
              />
              <span v-if="errors.lastName" class="error-message">{{ errors.lastName }}</span>
            </div>

            <div class="form-group">
              <label for="suffix">Suffix</label>
              <select
                id="suffix"
                v-model="suffix"
                :class="{ error: errors.suffix }"
                @change="validateSuffix"
                @blur="validateSuffix"
                @focus="suffixDropdownTouched = true"
                @click="suffixDropdownTouched = true"
              >
                <option value="">Select Suffix (Optional)</option>
                <option value="None">None</option>
                <option value="Jr.">Jr.</option>
                <option value="Sr.">Sr.</option>
                <option value="II">II</option>
                <option value="III">III</option>
                <option value="IV">IV</option>
                <option value="V">V</option>
                <option value="Other">Other</option>
              </select>
              <span v-if="errors.suffix" class="error-message">{{ errors.suffix }}</span>
            </div>

            <div class="form-group">
              <label for="email">Email <span class="required">*</span></label>
              <input
                id="email"
                v-model="email"
                type="email"
                required
                maxlength="254"
                :class="{ error: errors.email }"
                @input="validateEmail"
                @blur="validateEmail"
                placeholder="Enter your email address"
              />
              <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
            </div>

            <div class="form-group">
              <label for="dateOfBirth">Date of Birth <span class="required">*</span></label>
              <div class="date-input-wrapper">
                <input
                  id="dateOfBirth"
                  v-model="dateOfBirth"
                  type="date"
                  required
                  min="1950-01-01"
                  max="2015-12-31"
                  :class="{ error: errors.dateOfBirth }"
                  @input="validateDateOfBirth"
                  @blur="validateDateOfBirth"
                />
              </div>
              <span v-if="errors.dateOfBirth" class="error-message">{{ errors.dateOfBirth }}</span>
            </div>

            <div class="form-group">
              <label for="password">Password <span class="required">*</span></label>
              <div class="password-input-container">
                <input
                  id="password"
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  required
                  maxlength="64"
                  :class="{ error: errors.password }"
                  placeholder="Create a strong password"
                  @input="validatePassword"
                  @blur="validatePassword"
                />
                <button
                  type="button"
                  class="password-toggle"
                  @click="togglePasswordVisibility"
                  :aria-label="showPassword ? 'Hide password' : 'Show password'"
                >
                  <!-- Eye Slash Icon (when password is visible - click to hide) -->
                  <svg
                    v-if="showPassword"
                    class="eye-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                    <line
                      x1="4"
                      y1="6"
                      x2="20"
                      y2="18"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                  </svg>
                  <!-- Eye Icon (when password is hidden - click to show) -->
                  <svg
                    v-else
                    class="eye-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </button>
              </div>
              <div v-if="password" class="password-requirements">
                <div
                  class="requirement"
                  :class="{ valid: password.length >= 8 && password.length <= 64 }"
                >
                  <span class="checkmark">{{
                    password.length >= 8 && password.length <= 64 ? "✓" : "○"
                  }}</span>
                  Between 8 and 64 characters
                </div>
                <div class="requirement" :class="{ valid: /[a-z]/.test(password) }">
                  <span class="checkmark">{{ /[a-z]/.test(password) ? "✓" : "○" }}</span>
                  One lowercase letter
                </div>
                <div class="requirement" :class="{ valid: /[A-Z]/.test(password) }">
                  <span class="checkmark">{{ /[A-Z]/.test(password) ? "✓" : "○" }}</span>
                  One uppercase letter
                </div>
                <div class="requirement" :class="{ valid: /[0-9]/.test(password) }">
                  <span class="checkmark">{{ /[0-9]/.test(password) ? "✓" : "○" }}</span>
                  One number
                </div>
                <div class="requirement" :class="{ valid: hasSpecialChar(password) }">
                  <span class="checkmark">{{ hasSpecialChar(password) ? "✓" : "○" }}</span>
                  One special character
                </div>
              </div>
              <span v-if="errors.password" class="error-message">{{ errors.password }}</span>
            </div>

            <div class="form-group">
              <label for="confirmPassword">Confirm Password <span class="required">*</span></label>
              <div class="password-input-container">
                <input
                  id="confirmPassword"
                  v-model="confirmPassword"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  required
                  maxlength="64"
                  :class="{ error: errors.confirmPassword }"
                  placeholder="Confirm your password"
                  @input="validateConfirmPassword"
                  @blur="validateConfirmPassword"
                />
                <button
                  type="button"
                  class="password-toggle"
                  @click="toggleConfirmPasswordVisibility"
                  :aria-label="showConfirmPassword ? 'Hide password' : 'Show password'"
                >
                  <!-- Eye Slash Icon (when password is visible - click to hide) -->
                  <svg
                    v-if="showConfirmPassword"
                    class="eye-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                    <line
                      x1="4"
                      y1="6"
                      x2="20"
                      y2="18"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                  </svg>
                  <!-- Eye Icon (when password is hidden - click to show) -->
                  <svg
                    v-else
                    class="eye-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </button>
              </div>
              <span v-if="errors.confirmPassword" class="error-message">{{
                errors.confirmPassword
              }}</span>
            </div>
          </div>
        </div>

        <!-- Academic Background Section -->
        <div class="form-section">
          <div class="section-header">
            <div class="section-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-6"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5"
                />
              </svg>
            </div>
            <div class="section-title">
              <h3>Academic Background</h3>
              <p>Your current academic information</p>
            </div>
          </div>

          <div class="form-group">
            <label for="school">School Name <span class="required">*</span></label>
            <input
              id="school"
              v-model="school"
              type="text"
              required
              maxlength="150"
              :class="{ error: errors.school }"
              placeholder="Enter your school name"
              @input="validateSchoolName"
              @blur="validateSchoolName"
            />
            <div class="important-note">
              <strong>Important:</strong> Please enter the full name of your school. Please avoid abbreviations. Example: "University of Santo Tomas" not "UST"
            </div>
            <span v-if="errors.school" class="error-message">{{ errors.school }}</span>
          </div>

          <div class="form-grid">
            <div class="form-group">
              <label for="gradeLevel">Grade Level <span class="required">*</span></label>
              <select
                id="gradeLevel"
                v-model="gradeLevel"
                required
                :class="{ error: errors.gradeLevel }"
                @change="hasUserInteracted = true"
              >
                <option value="">Select your grade level</option>
                <option value="11th">11th Grade</option>
                <option value="12th">12th Grade</option>
              </select>
              <span v-if="errors.gradeLevel" class="error-message">{{ errors.gradeLevel }}</span>
            </div>

            <div class="form-group">
              <label for="academicTrack">Academic Track <span class="required">*</span></label>
              <select
                id="academicTrack"
                v-model="academicTrack"
                required
                :class="{ error: errors.academicTrack }"
                @change="hasUserInteracted = true"
              >
                <option value="">Select your academic track</option>
                <option value="stem">STEM</option>
                <option value="humss">HUMSS</option>
                <option value="abm">ABM</option>
                <option value="gas">GAS</option>
                <option value="tvl">TVL</option>
              </select>
              <span v-if="errors.academicTrack" class="error-message">{{
                errors.academicTrack
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Continue Button (Outside the form card) -->
      <button
        @click="handleSubmit"
        :disabled="isLoading || !isFormValid"
        class="continue-btn"
        :class="{ disabled: !isFormValid }"
      >
        <span>Continue to Subject Selection</span>
        <svg class="arrow-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
        </svg>
      </button>

      <!-- Form Status Message -->
      <!-- <div v-if="!isFormValid && !isLoading && hasUserInteracted" class="form-status">
        <svg class="status-icon" viewBox="0 0 24 24" fill="currentColor">
          <path
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
          />
        </svg>
        <span>Please complete all required fields to continue</span>
      </div>

      <!-- Debug Panel (temporary) -->
      <!-- <div v-if="!isFormValid && !isLoading && hasUserInteracted" class="debug-panel">
        <h4>Form Validation Status:</h4>
        <ul>
          <li :class="{ valid: firstName.trim().length >= 2 }">
            First Name: {{ firstName.trim().length >= 2 ? "✓" : "✗" }}
          </li>
          <li :class="{ valid: lastName.trim().length >= 2 }">
            Last Name: {{ lastName.trim().length >= 2 ? "✓" : "✗" }}
          </li>
          <li
            :class="{
              valid: email.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !errors.email,
            }"
          >
            Email:
            {{
              email.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !errors.email ? "✓" : "✗"
            }}
            <span v-if="errors.email" class="error-text">({{ errors.email }})</span>
          </li>
          <li :class="{ valid: dateOfBirth && !errors.dateOfBirth }">
            Date of Birth: {{ dateOfBirth && !errors.dateOfBirth ? "✓" : "✗" }}
            <span v-if="errors.dateOfBirth" class="error-text">({{ errors.dateOfBirth }})</span>
          </li>
          <li :class="{ valid: password && !errors.password }">
            Password: {{ password && !errors.password ? "✓" : "✗" }}
            <span v-if="errors.password" class="error-text">({{ errors.password }})</span>
          </li>
          <li :class="{ valid: password === confirmPassword && confirmPassword }">
            Confirm Password: {{ password === confirmPassword && confirmPassword ? "✓" : "✗" }}
          </li>
          <li :class="{ valid: school.trim() }">School: {{ school.trim() ? "✓" : "✗" }}</li>
          <li :class="{ valid: gradeLevel }">Grade Level: {{ gradeLevel ? "✓" : "✗" }}</li>
          <li :class="{ valid: academicTrack }">Academic Track: {{ academicTrack ? "✓" : "✗" }}</li>
        </ul>
      </div> -->
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, watch, onMounted } from "vue";
import {
  auth,
  db,
  fetchSignInMethodsForEmail,
} from "@/firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { useStudentRegistrationStorage, type StudentInfoData } from "@/composables/useRegistrationStorage";
import { logDebug } from '@/utils/secureLogger';

// Props
interface Props {
  initialData?: {
    firstName: string;
    middleName: string;
    lastName: string;
    suffix: string;
    email: string;
    dateOfBirth: string;
    password: string;
    school: string;
    gradeLevel: string;
    academicTrack: string;
    isGoogleUser?: boolean;
    googleUid?: string;
  } | null;
}

const props = defineProps<Props>();

// Emits
const emit = defineEmits<{
  "student-info-academic-complete": [
    data: {
      firstName: string;
      middleName: string;
      lastName: string;
      suffix: string;
      email: string;
      dateOfBirth: string;
      password: string;
      school: string;
      gradeLevel: string;
      academicTrack: string;
      isGoogleUser?: boolean;
      googleUid?: string;
      firebaseUid?: string;
    }
  ];
}>();

// Form data - Personal Information
const firstName = ref("");
const middleName = ref("");
const lastName = ref("");
const suffix = ref("");
const email = ref("");
const dateOfBirth = ref("");
const password = ref("");
const confirmPassword = ref("");
const showPassword = ref(false);
const showConfirmPassword = ref(false);

// Form data - Academic Background
const school = ref("");
const gradeLevel = ref("");
const academicTrack = ref("");

// State
const isLoading = ref(false);
const errorMessage = ref("");
const isFormValid = ref(false);
const hasUserInteracted = ref(false);
const suffixDropdownTouched = ref(false);

// Validation errors
const errors = reactive({
  firstName: "",
  middleName: "",
  lastName: "",
  suffix: "",
  email: "",
  dateOfBirth: "",
  password: "",
  confirmPassword: "",
  school: "",
  gradeLevel: "",
  academicTrack: "",
});

// Initialize registration storage
const { saveStudentInfo, loadStudentInfo } = useStudentRegistrationStorage();

// Load saved data on mount
onMounted(() => {
  // First try to load from props (when navigating back)
  if (props.initialData) {
    logDebug('Restoring student info from props');
    firstName.value = props.initialData.firstName || "";
    middleName.value = props.initialData.middleName || "";
    lastName.value = props.initialData.lastName || "";
    suffix.value = props.initialData.suffix || "";
    email.value = props.initialData.email || "";
    dateOfBirth.value = props.initialData.dateOfBirth || "";
    school.value = props.initialData.school || "";
    gradeLevel.value = props.initialData.gradeLevel || "";
    academicTrack.value = props.initialData.academicTrack || "";
    // Note: passwords are not restored from props for security
  } else {
    // If no props, try to load from sessionStorage
    const savedData = loadStudentInfo();
    if (savedData) {
      logDebug('Restoring student info from sessionStorage');
      firstName.value = savedData.firstName || "";
      middleName.value = savedData.middleName || "";
      lastName.value = savedData.lastName || "";
      suffix.value = savedData.suffix || "";
      email.value = savedData.email || "";
      dateOfBirth.value = savedData.dateOfBirth || "";
      school.value = savedData.school || "";
      gradeLevel.value = savedData.gradeLevel || "";
      academicTrack.value = savedData.academicTrack || "";
    }
  }
});

// Auto-save form data to sessionStorage on any change (excluding passwords)
watch([firstName, middleName, lastName, suffix, email, dateOfBirth, school, gradeLevel, academicTrack], () => {
  const dataToSave: StudentInfoData = {
    firstName: firstName.value,
    middleName: middleName.value,
    lastName: lastName.value,
    suffix: suffix.value,
    email: email.value,
    dateOfBirth: dateOfBirth.value,
    school: school.value,
    gradeLevel: gradeLevel.value,
    academicTrack: academicTrack.value,
  };
  saveStudentInfo(dataToSave);
}, { deep: true });

// Name validation regex (letters, dash, apostrophe only)
const nameRegex = /^[a-zA-Z\s\-']+$/;

// Real-time name validation
const validateNameField = (
  value: string,
  fieldName: keyof typeof errors,
  minLength: number = 2,
  maxLength: number = 50
) => {
  hasUserInteracted.value = true;

  if (!value.trim()) {
    if (fieldName === "firstName" || fieldName === "lastName") {
      errors[fieldName] = `Please enter your ${fieldName === "firstName" ? "First" : "Last"} Name.`;
    }
    return false;
  }

  const trimmedValue = value.trim();

  // Check length
  if (trimmedValue.length < minLength) {
    errors[fieldName] = `${
      fieldName === "firstName" ? "First" : fieldName === "lastName" ? "Last" : "Middle"
    } name must be at least ${minLength} characters long`;
    return false;
  }

  if (trimmedValue.length > maxLength) {
    errors[fieldName] = `${
      fieldName === "firstName" ? "First" : fieldName === "lastName" ? "Last" : "Middle"
    } name must be no more than ${maxLength} characters long`;
    return false;
  }

  // Check allowed characters
  if (!nameRegex.test(trimmedValue)) {
    errors[fieldName] = `${
      fieldName === "firstName" ? "First" : fieldName === "lastName" ? "Last" : "Middle"
    } name can only contain letters, spaces, dashes (-), and apostrophes (')`;
    return false;
  }

  errors[fieldName] = "";
  return true;
};

// Suffix validation
const validateSuffix = () => {
  hasUserInteracted.value = true;

  if (!suffix.value) {
    // If dropdown was touched but no selection made, show error
    if (suffixDropdownTouched.value) {
      errors.suffix = "Please click None if you have no suffix.";
      return false;
    }
    errors.suffix = "";
    return true; // Suffix is optional
  }

  // If "None" is selected, that's valid
  if (suffix.value === "None") {
    errors.suffix = "";
    return true;
  }

  // If "Other" is selected, we could add additional validation here
  // For now, any selected suffix is valid
  errors.suffix = "";
  return true;
};

// School name validation
const validateSchoolName = () => {
  hasUserInteracted.value = true;

  if (!school.value.trim()) {
    errors.school = "School name is required";
    return false;
  }

  const trimmedSchool = school.value.trim();

  // Check length
  if (trimmedSchool.length < 4) {
    errors.school = "School name must be at least 4 characters long";
    return false;
  }

  if (trimmedSchool.length > 150) {
    errors.school = "School name must be no more than 150 characters long";
    return false;
  }

  // Check allowed characters (letters, numbers, spaces, hyphens, periods, apostrophes, ampersands, commas, parentheses)
  const schoolRegex = /^[a-zA-Z0-9\s\-.'&,()]+$/;
  if (!schoolRegex.test(trimmedSchool)) {
    errors.school = "School name can only contain letters, numbers, spaces, hyphens, periods, apostrophes, and common punctuation";
    return false;
  }

  errors.school = "";
  return true;
};

// Watch for initial data changes and populate form
watch(
  () => props.initialData,
  (newData) => {
    if (newData) {
      firstName.value = newData.firstName || "";
      middleName.value = newData.middleName || "";
      lastName.value = newData.lastName || "";
      suffix.value = newData.suffix || "";
      email.value = newData.email || "";
      dateOfBirth.value = newData.dateOfBirth || "";
      password.value = newData.password || "";
      confirmPassword.value = newData.password || ""; // Pre-fill confirm password
      school.value = newData.school || "";
      gradeLevel.value = newData.gradeLevel || "";
      academicTrack.value = newData.academicTrack || "";
    }
  },
  { immediate: true }
);

// Watch for form field changes to update validation
watch(
  [
    firstName,
    lastName,
    email,
    dateOfBirth,
    password,
    confirmPassword,
    school,
    gradeLevel,
    academicTrack,
  ],
  () => {
    checkFormValidity();
  },
  { deep: true }
);

// Validation functions
const validateForm = async () => {
  let isValid = true;

  // Reset errors
  Object.keys(errors).forEach((key) => {
    errors[key as keyof typeof errors] = "";
  });

  // First Name validation
  if (!validateNameField(firstName.value, "firstName")) {
    isValid = false;
  }

  // Last Name validation
  if (!validateNameField(lastName.value, "lastName")) {
    isValid = false;
  }

  // Email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!email.value.trim()) {
    errors.email = "Email is required";
    isValid = false;
  } else if (!emailRegex.test(email.value)) {
    errors.email = "Please enter a valid email";
    isValid = false;
  } else {
    // Check if email already exists
    const emailExists = await validateEmailExists(email.value);
    if (emailExists) {
      errors.email = "This email is already registered";
      isValid = false;
    }
  }

  // Date of Birth validation
  if (!dateOfBirth.value) {
    errors.dateOfBirth = "Date of birth is required";
    isValid = false;
  } else {
    const today = new Date();
    const birthDate = new Date(dateOfBirth.value);

    // Check if the date is valid
    if (isNaN(birthDate.getTime())) {
      errors.dateOfBirth = "Please enter a valid date";
      isValid = false;
    } else {
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      if (age < 16) {
        errors.dateOfBirth = "must be at least 16 years old to register";
        isValid = false;
      }
    }
  }

  // Password validation
  if (!password.value) {
    errors.password = "Password is required";
    isValid = false;
  } else {
    const passwordErrors = validatePasswordStrength(password.value);
    if (passwordErrors.length > 0) {
      // Only show basic error message, not detailed requirements
      errors.password = "Password does not meet security requirements";
      isValid = false;
    } else {
      errors.password = ""; // Clear error if password is strong
    }
  }

  // Confirm password validation
  if (!confirmPassword.value) {
    errors.confirmPassword = "Please confirm your password";
    isValid = false;
  } else if (password.value !== confirmPassword.value) {
    errors.confirmPassword = "Passwords do not match";
    isValid = false;
  }

  // School validation
  if (!validateSchoolName()) {
    isValid = false;
  }

  // Grade Level validation
  if (!gradeLevel.value) {
    errors.gradeLevel = "Grade level is required";
    isValid = false;
  }

  // Academic Track validation
  if (!academicTrack.value) {
    errors.academicTrack = "Academic track is required";
    isValid = false;
  }

  return isValid;
};

// Password strength validation
const validatePasswordStrength = (password: string): string[] => {
  const errors: string[] = [];

  if (password.length < 8) {
    errors.push("Password must be at least 8 characters long");
  }

  if (password.length > 64) {
    errors.push("Password must be no more than 64 characters");
  }

  if (!/[a-z]/.test(password)) {
    errors.push("Password must contain at least one lowercase letter");
  }

  if (!/[A-Z]/.test(password)) {
    errors.push("Password must contain at least one uppercase letter");
  }

  if (!/[0-9]/.test(password)) {
    errors.push("Password must contain at least one number");
  }

  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push("Password must contain at least one special character");
  }

  return errors;
};

// Helper function to check for special characters
const hasSpecialChar = (password: string): boolean => {
  return /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
};

// Password visibility toggle functions
const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value;
};

const toggleConfirmPasswordVisibility = () => {
  showConfirmPassword.value = !showConfirmPassword.value;
};

// Email validation function using Firebase Auth
const validateEmailExists = async (emailToCheck: string): Promise<boolean> => {
  if (!emailToCheck || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailToCheck)) {
    return false;
  }

  try {
    // Method 1: Check Firestore users collection first (more reliable)
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("email", "==", emailToCheck));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      // Email exists in Firestore
      return true;
    }

    // Method 2: Try fetchSignInMethodsForEmail as backup
    const methods = await fetchSignInMethodsForEmail(auth, emailToCheck);
    if (Array.isArray(methods) && methods.length > 0) {
      return true;
    }

    // Email doesn't exist
    return false;
  } catch (error: any) {
    console.error("Email validation error:", error.code, error.message);
    // On error, assume email doesn't exist to allow registration attempt
    return false;
  }
};

// Debounce timer for email validation
let emailValidationTimer: number | null = null;

// Real-time email validation
const validateEmail = async () => {
  hasUserInteracted.value = true;

  // Clear existing timer
  if (emailValidationTimer) {
    clearTimeout(emailValidationTimer);
  }

  // Basic validation first
  if (!email.value) {
    errors.email = "Email is required";
    return;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.value)) {
    errors.email = "Please enter a valid email";
    return;
  }

  // Clear format errors but keep any existing "already registered" errors
  if (errors.email === "Please enter a valid email") {
    errors.email = "";
  }

  // Debounce the API call to avoid too many requests
  emailValidationTimer = Number(
    setTimeout(async () => {
      try {
        const emailExists = await validateEmailExists(email.value);

        if (emailExists) {
          errors.email = "This email is already registered";
        } else {
          // Only clear if there was an "already registered" error
          if (errors.email === "This email is already registered") {
            errors.email = "";
          }
        }

        // Update form validity after email validation
        checkFormValidity();
      } catch (error) {
        console.error("Email validation error:", error);
        // Don't show error for network issues, just log them
      }
    }, 500)
  ); // Wait 500ms after user stops typing
};

// Real-time password validation
const validatePassword = () => {
  hasUserInteracted.value = true;

  if (!password.value) {
    errors.password = "Password is required";
    return;
  }

  const passwordErrors = validatePasswordStrength(password.value);
  if (passwordErrors.length > 0) {
    // Only show basic error message, not detailed requirements
    errors.password = "Password does not meet security requirements";
  } else {
    errors.password = ""; // Clear error if password is strong
  }
};

// Real-time confirm password validation
const validateConfirmPassword = () => {
  hasUserInteracted.value = true;

  if (!confirmPassword.value) {
    errors.confirmPassword = "Please confirm your password";
    return;
  }
  errors.confirmPassword = password.value === confirmPassword.value ? "" : "Passwords do not match";
};

// Real-time date of birth validation
const validateDateOfBirth = () => {
  hasUserInteracted.value = true;

  if (!dateOfBirth.value) {
    errors.dateOfBirth = "Date of birth is required";
    return;
  }

  const birthDate = new Date(dateOfBirth.value);

  // Check if the date is valid
  if (isNaN(birthDate.getTime())) {
    errors.dateOfBirth = "Please enter a valid date";
    return;
  }

  const birthYear = birthDate.getFullYear();

  // Check year range (1950-2015)
  if (birthYear < 1950) {
    errors.dateOfBirth = "Birth year must be 1950 or later";
    return;
  }

  if (birthYear > 2015) {
    errors.dateOfBirth = "Birth year must be 2015 or earlier";
    return;
  }

  // Check if date is not in the future
  const today = new Date();
  if (birthDate > today) {
    errors.dateOfBirth = "Date of birth cannot be in the future";
    return;
  }

  // Clear error if all validations pass
  errors.dateOfBirth = "";
};

// Check if all required fields are valid
const checkFormValidity = () => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Check all required fields
  const hasFirstName = Boolean(firstName.value.trim() && !errors.firstName);
  const hasLastName = Boolean(lastName.value.trim() && !errors.lastName);
  const hasValidEmail = Boolean(
    email.value.trim() && emailRegex.test(email.value) && !errors.email
  );
  const hasValidDateOfBirth = Boolean(dateOfBirth.value && !errors.dateOfBirth);
  const hasValidPassword = Boolean(password.value && !errors.password);
  const hasMatchingPasswords = Boolean(
    password.value === confirmPassword.value && confirmPassword.value
  );
  const hasSchool = Boolean(school.value.trim() && !errors.school);
  const hasGradeLevel = Boolean(gradeLevel.value);
  const hasAcademicTrack = Boolean(academicTrack.value);

  // Form validation logic (debug logging removed)

  const isValid =
    hasFirstName &&
    hasLastName &&
    hasValidEmail &&
    hasValidDateOfBirth &&
    hasValidPassword &&
    hasMatchingPasswords &&
    hasSchool &&
    hasGradeLevel &&
    hasAcademicTrack;

  isFormValid.value = isValid;
};

// Create user in Firebase and store data in Firestore

// Handle form submission
const handleSubmit = async () => {
  // Check if form is valid
  if (!isFormValid.value) {
    return;
  }

  // Check for age validation error specifically
  if (errors.dateOfBirth && errors.dateOfBirth.includes("must be at least 16 years old")) {
    return;
  }

  if (!(await validateForm())) {
    return;
  }

  // Just emit the data - no Firebase user creation yet
  const emitData = {
    firstName: firstName.value,
    middleName: middleName.value,
    lastName: lastName.value,
    suffix: suffix.value,
    email: email.value,
    dateOfBirth: dateOfBirth.value,
    password: password.value,
    school: school.value,
    gradeLevel: gradeLevel.value,
    academicTrack: academicTrack.value,
  };

  emit("student-info-academic-complete", emitData);
};

// Register with Google
const handleGoogleRegister = async () => {
  // For now, just emit the data without Firebase user creation
  // Firebase user creation will happen after email verification
  const emitData = {
    firstName: firstName.value || "Google",
    middleName: middleName.value,
    lastName: lastName.value || "User",
    suffix: suffix.value,
    email: email.value,
    dateOfBirth: dateOfBirth.value,
    password: "google_auth_" + Math.random().toString(36).substr(2, 9),
    school: school.value,
    gradeLevel: gradeLevel.value,
    academicTrack: academicTrack.value,
    isGoogleUser: true,
  };

  emit("student-info-academic-complete", emitData);
};
</script>

<style scoped>
.registration-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: #f5f5f5;
  padding: 0;
  padding-top: 5px; /* Add top padding to account for header height */
  position: relative; /* Needed for decorative elements */
  overflow: hidden; /* Hide overflow for decorative elements */
}

.decorative-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1; /* Ensure it's behind other content */
  pointer-events: none; /* Allow clicks to pass through */
}

.shape {
  position: absolute;
  background: linear-gradient(45deg, #4f46e5, #10b981);
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.1;
}

.shape-1 {
  width: 300px;
  height: 300px;
  top: -50px;
  left: -50px;
}

.shape-2 {
  width: 200px;
  height: 200px;
  bottom: 100px;
  right: 100px;
}

.shape-3 {
  width: 400px;
  height: 400px;
  top: 300px;
  left: 50%;
  transform: translateX(-50%);
}

.star {
  position: absolute;
  font-size: 24px;
  color: #f59e0b;
  opacity: 0.2;
}

.star-1 {
  top: 10%;
  left: 20%;
}

.star-2 {
  bottom: 20%;
  right: 30%;
}

.flower {
  position: absolute;
  font-size: 40px;
  color: #f59e0b;
  opacity: 0.1;
}

.flower-1 {
  top: 50%;
  left: 10%;
}

.flower-2 {
  bottom: 10%;
  right: 20%;
}

.progress-section {
  position: absolute;
  top: 100px; /* Adjust based on header height */
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 800px;
  padding: 0 20px;
  box-sizing: border-box;
}

.progress-container {
  display: flex;
  align-items: center;
  justify-content: space-around;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  background: #e0e0e0;
  border-radius: 10px;
  padding: 5px;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

.progress-step {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #1e40af;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
  z-index: 1;
}

.progress-step.active {
  background: #1e40af;
  color: white;
}

.progress-line {
  flex-grow: 1;
  height: 2px;
  background: #e0e0e0;
  position: relative;
}

.progress-line::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  height: 100%;
  background: #1e40af;
  transform: translateY(-50%);
  z-index: -1;
}

.content-wrapper {
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  padding: 0;
  width: 100%;
  max-width: 1000px;
  position: relative; /* Needed for decorative elements */
  z-index: 1; /* Ensure it's above decorative elements */
  margin-top: 40px; /* Reduced space to bring containers closer to steps */
}

.header-section {
  text-align: center;
  margin-bottom: 44px;
  margin-top: 20px;
}

.main-title {
  color: #1e40af;
  font-size: 40px;
  font-weight: 700;
  margin-bottom: 10px;
}

.subtitle {
  color: #3b82f6;
  font-size: 18px;
  margin-bottom: 20px;
}

.form-card {
  background: transparent;
  border-radius: 0;
  box-shadow: none;
  padding: 0;
  width: 100%;
  max-width: 1000px;
  margin: 20px auto 20px auto; /* Proper spacing to prevent cutting */
}

.form-section {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 36px;
  margin-bottom: 28px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  gap: 16px;
  padding: 18px;
  padding-bottom: 18px;
  border-bottom: none;
  background-color: transparent;
  border-radius: 0;
  margin: -36px -36px 16px -36px;
}

.section-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
}

.section-icon svg {
  width: 28px;
  height: 28px;
}

.section-title h3 {
  margin: 0;
  color: #1e40af;
  font-size: 20px;
  font-weight: 600;
}

.section-title p {
  margin: 4px 0 0 0;
  color: #3b82f6;
  font-size: 14px;
  font-style: normal;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 28px;
  margin-top: 28px;
}

.form-group {
  margin-bottom: 24px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #374151;
  font-weight: 600;
  font-size: 14px;
}

.required {
  color: #dc2626;
  font-weight: 700;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
  background: #fafafa;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #3861fb;
  box-shadow: 0 0 0 3px rgba(59, 97, 251, 0.1);
  background: white;
}

.form-group input.error,
.form-group select.error {
  border-color: #dc2626;
}

.date-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.important-note {
  background: #eff6ff;
  border: 1px solid #3b82f6;
  color: #1e40af;
  padding: 12px;
  border-radius: 6px;
  margin-top: 8px;
  font-size: 13px;
  line-height: 1.4;
}

.error-message {
  color: #dc2626;
  font-size: 14px;
  margin-top: 5px;
  display: block;
}

.password-requirements {
  margin-top: 8px;
  padding: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 13px;
}

.requirement {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  color: #64748b;
  transition: color 0.2s ease;
}

.requirement:last-child {
  margin-bottom: 0;
}

.requirement.valid {
  color: #059669;
}

.checkmark {
  margin-right: 8px;
  font-weight: bold;
  width: 16px;
  text-align: center;
}

.requirement.valid .checkmark {
  color: #059669;
}

.password-input-container {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input-container input {
  padding-right: 45px;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: color 0.2s ease;
}

.password-toggle:hover {
  color: #374151;
}

.eye-icon {
  width: 20px;
  height: 20px;
}

.global-error {
  background: #fdf2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 20px;
  text-align: center;
}

.continue-btn {
  width: 35%;
  padding: 16px 20px;
  background: #5271ff;
  color: white;
  border: 2px solid #000000;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  margin: 0 0 20px auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.continue-btn:hover:not(:disabled) {
  background: #4c63e6;
  border-color: #000000;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.continue-btn:disabled,
.continue-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #9ca3af;
  transform: none;
}

.continue-btn.disabled:hover {
  background: #9ca3af;
  transform: none;
}

.arrow-icon {
  width: 20px;
  height: 20px;
}

.form-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
  padding: 12px 16px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  color: #92400e;
  font-size: 14px;
  font-weight: 500;
}

.status-icon {
  width: 16px;
  height: 16px;
  color: #f59e0b;
}

.debug-panel {
  margin-top: 16px;
  padding: 16px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 12px;
}

.debug-panel h4 {
  margin: 0 0 12px 0;
  color: #374151;
  font-size: 14px;
}

.debug-panel ul {
  margin: 0;
  padding: 0;
  list-style: none;
}

.debug-panel li {
  margin-bottom: 4px;
  color: #dc2626;
  font-family: monospace;
}

.debug-panel li.valid {
  color: #059669;
}

.debug-panel .error-text {
  color: #dc2626;
  font-style: italic;
}

.google-section {
  margin-top: 30px;
  text-align: center;
}

.divider {
  text-align: center;
  margin: 20px 0;
  position: relative;
}

.divider::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: #e5e7eb;
}

.divider span {
  background: white;
  padding: 0 15px;
  color: #6b7280;
  font-size: 14px;
}

.google-btn {
  width: 100%;
  padding: 12px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 20px;
}

.google-btn:hover:not(:disabled) {
  border-color: #4285f4;
  background: #f8f9fa;
}

.google-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.google-icon {
  width: 20px;
  height: 20px;
}

.footer-links {
  margin-top: 30px;
  text-align: center;
}

.login-link {
  text-align: center;
  color: #6b7280;
  font-size: 14px;
  margin-bottom: 15px;
}

.login-link a {
  color: #1e40af;
  text-decoration: none;
  font-weight: 500;
}

.login-link a:hover {
  text-decoration: underline;
}

.user-type-link {
  text-align: center;
  color: #6b7280;
  font-size: 14px;
}

.user-type-link a {
  color: #1e40af;
  text-decoration: none;
  font-weight: 500;
}

.user-type-link a:hover {
  text-decoration: underline;
}

/* Responsive Design */
@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .registration-container {
    padding-top: 150px; /* Adjust for header height on smaller screens */
  }

  .progress-section {
    top: 150px; /* Adjust for header height on smaller screens */
  }

  .content-wrapper {
    padding: 24px;
    margin: 10px;
  }

  .main-title {
    font-size: 32px;
  }

  .subtitle {
    font-size: 16px;
  }

  .progress-container {
    max-width: 400px;
  }

  .progress-step {
    width: 36px;
    height: 36px;
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .registration-container {
    padding: 20px 10px;
    padding-top: 120px;
  }

  .progress-section {
    top: 120px;
    padding: 0 10px;
  }

  .content-wrapper {
    padding: 20px;
    margin: 5px;
  }

  .main-title {
    font-size: 28px;
  }

  .subtitle {
    font-size: 14px;
  }

  .progress-container {
    max-width: 300px;
  }

  .progress-step {
    width: 32px;
    height: 32px;
    font-size: 12px;
  }

  .section-icon {
    width: 40px;
    height: 40px;
  }

  .section-icon svg {
    width: 20px;
    height: 20px;
  }
}
</style>
